/**
 * Customer-facing SCIM/SSO self-service API (Safe+ / ASM).
 *
 * Mounted at ``/api/v2/sso/`` by the AAS backend. This service is the
 * companion to ``ssoConfig.service.ts`` (which manages SAMLConfig XML
 * import/export) and drives the SSO config UI under
 * /account?tab=people&subtab=sso.
 *
 * Typed `@tiaki/http-client` surface per ADR 113 §Decision.
 *
 * Pattern:
 *   const { data, error } = await client.GET('/api/v2/sso/...', {
 *     params: { path: { id } as never },
 *   });
 *   if (error) throw error;
 *   return data;
 *
 * `as never` casts on params/body bridge the drf-spectacular schema's looser
 * shapes (notably FormData and synthetic-field uploads).
 *
 * Security contract (see backend: server/apps/scim/api/serializers.py):
 *   - Responses NEVER include bearer_token / idp_credentials / webhook_secret.
 *   - Writes paste credentials via raw_* fields; the server hashes / stores.
 *   - Status responses are aggregate counts only — no user-level rows.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { client } from '../lib/http-client';

function unwrap<T>(result: { data?: T; error?: unknown }): T {
  if (result.error !== undefined) {
    throw result.error;
  }
  return result.data as T;
}

// ── Types ────────────────────────────────────────────────────────────

export type ScimIdpType = 'okta' | 'azure' | 'google' | 'duo';
export type ScimSyncStatus = 'pending' | 'success' | 'failed';

export interface ScimConfigRow {
  id: number;
  idp_name: string;
  idp_type: ScimIdpType;
  app_name: string;
  enabled: boolean;
  last_sync: string | null;
  sync_status: ScimSyncStatus;
  saml_config: number | null;
  saml_config_name: string | null;
  has_bearer_token: boolean;
  has_credentials: boolean;
}

export interface RoleMapping {
  group_name: string;
  role_name: string;
}

export interface SchoolMapping {
  group_name: string;
  school_name: string;
}

export interface SamlConfigSummary {
  id: number;
  name: string;
  user_hostname_match: string;
  app_name: string | null;
  type: string | null;
  roles_summary: number;
  sites_summary: boolean;
}

export interface CredentialShape {
  description: string;
  required_keys: string[];
  example_keys: string[];
}

/**
 * Tiaki SP details computed per-request from SAMLConfig + settings.
 * Phase 1 of the ADR 019 self-service surface — answers the
 * "what URL do I put in Okta?" support ticket without engineering.
 * Backend: server/apps/scim/api/sp_details.py.
 */
export interface SpDetails {
  sp_entity_id: string;
  acs_url: string;
  slo_url: string;
  sp_metadata_url: string;
  scim_base_url: string;
  user_hostname_match: string;
  app_name: string;
}

export interface ScimConfigDetail {
  id: number;
  idp_name: string;
  idp_type: ScimIdpType;
  app_name: string;
  enabled: boolean;
  saml_config: number | null;
  saml_config_summary: SamlConfigSummary | null;
  uses_saml_mappings: boolean;
  polling_enabled: boolean;
  polling_interval_minutes: number;
  role_mappings: RoleMapping[];
  school_mappings: SchoolMapping[];
  credential_shape: CredentialShape | null;
  /** Tiaki SP URLs for the SSO Admin to copy into their IdP. Null when no
   *  SAMLConfig is linked (engineering-managed configurations only). */
  sp_details: SpDetails | null;
  last_sync: string | null;
  sync_status: ScimSyncStatus;
  has_bearer_token: boolean;
  has_credentials: boolean;
  has_webhook_secret: boolean;
  /** Google only — public super-admin email used for domain-wide-delegation
   *  impersonation. Not a secret, so it's exposed on read. */
  admin_email: string;
  // Present on create responses only (one-time disclosure).
  generated_bearer_token?: string;
}

export interface ScimConfigWrite {
  idp_name?: string;
  idp_type?: ScimIdpType;
  app_name?: string;
  enabled?: boolean;
  saml_config?: number | null;
  polling_enabled?: boolean;
  polling_interval_minutes?: number;
  role_mappings?: RoleMapping[];
  school_mappings?: SchoolMapping[];
  // Write-only paste fields
  raw_bearer_token?: string;
  raw_idp_credentials?: Record<string, unknown>;
  raw_webhook_secret?: string;
  /** Google only — merges into idp_credentials.admin_email without
   *  replacing the rest of the creds dict. */
  update_admin_email?: string;
}

export interface ScimSyncStatusResponse {
  last_sync: string | null;
  sync_status: ScimSyncStatus;
  polling_enabled: boolean;
  polling_interval_minutes: number;
  users_synced: number;
  failures_last_hour: number;
}

export interface TestConnectionResponse {
  success: boolean;
  message: string;
  sample_count: number;
}

export interface AvailableRole {
  id: number;
  name: string;
}

export interface AvailableSchool {
  id: number;
  name: string;
}

export interface SsoCapabilities {
  is_sso_admin: boolean;
  is_staff: boolean;
  email_domain: string | null;
}

// DRF pagination envelope
interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// ── Calls ────────────────────────────────────────────────────────────

export async function getSsoCapabilities(): Promise<SsoCapabilities> {
  const result = await client.GET('/api/v2/sso/capabilities/', {});
  return unwrap(result) as unknown as SsoCapabilities;
}

export async function listScimConfigs(): Promise<ScimConfigRow[]> {
  const result = await client.GET('/api/v2/sso/scim-configs/', {});
  const res = unwrap(result) as unknown as Paginated<ScimConfigRow> | ScimConfigRow[];
  return Array.isArray(res) ? res : res.results;
}

export async function getScimConfig(id: number): Promise<ScimConfigDetail> {
  const result = await client.GET('/api/v2/sso/scim-configs/{id}/', {
    params: { path: { id } as never },
  });
  return unwrap(result) as unknown as ScimConfigDetail;
}

export async function createScimConfig(
  payload: ScimConfigWrite,
): Promise<ScimConfigDetail> {
  const result = await client.POST('/api/v2/sso/scim-configs/', {
    body: payload as never,
  });
  return unwrap(result) as unknown as ScimConfigDetail;
}

export async function updateScimConfig(
  id: number,
  payload: ScimConfigWrite,
): Promise<ScimConfigDetail> {
  const result = await client.PATCH('/api/v2/sso/scim-configs/{id}/', {
    params: { path: { id } as never },
    body: payload as never,
  });
  return unwrap(result) as unknown as ScimConfigDetail;
}

// Soft-delete via SafeDeleteModel — the row drops out of the admin list and
// active queries but is recoverable by engineering. Distinct from
// setScimConfigEnabled (which pauses provisioning without hiding the row).
export async function deleteScimConfig(id: number): Promise<void> {
  const result = await client.DELETE('/api/v2/sso/scim-configs/{id}/', {
    params: { path: { id } as never },
  });
  const __resp = result as { error?: unknown }; if (__resp.error !== undefined) throw __resp.error;
}

// Pause/resume provisioning without touching the saved row. Reversible by
// the admin from the same drawer.
export async function setScimConfigEnabled(
  id: number,
  enabled: boolean,
): Promise<ScimConfigDetail> {
  const result = await client.PATCH('/api/v2/sso/scim-configs/{id}/', {
    params: { path: { id } as never },
    body: { enabled } as never,
  });
  return unwrap(result) as unknown as ScimConfigDetail;
}

export async function testScimConnection(
  id: number,
): Promise<TestConnectionResponse> {
  // The drf-spectacular schema for `/api/v2/sso/scim-configs/{id}/test/` POST
  // expects a body — the original migration was missing it. Pass an empty
  // body via a permissive widen so the path-typing still gates the URL.
  const result = await (
    client.POST as unknown as (
      path: string,
      init: unknown,
    ) => ReturnType<typeof client.POST>
  )('/api/v2/sso/scim-configs/{id}/test/', {
    params: { path: { id: String(id) } },
    body: {},
  });
  return unwrap(result) as unknown as TestConnectionResponse;
}

export async function triggerScimSync(
  id: number,
): Promise<{ detail: string; task_id?: string }> {
  // Same body-required + path-shape divergence as testScimConnection.
  const result = await (
    client.POST as unknown as (
      path: string,
      init: unknown,
    ) => ReturnType<typeof client.POST>
  )('/api/v2/sso/scim-configs/{id}/sync/', {
    params: { path: { id: String(id) } },
    body: {},
  });
  return unwrap(result) as unknown as { detail: string; task_id?: string };
}

export async function getScimSyncStatus(
  id: number,
): Promise<ScimSyncStatusResponse> {
  const result = await client.GET('/api/v2/sso/scim-configs/{id}/status/', {
    params: { path: { id } as never },
  });
  return unwrap(result) as unknown as ScimSyncStatusResponse;
}

export async function listLinkableSamlConfigs(): Promise<SamlConfigSummary[]> {
  const result = await client.GET('/api/v2/sso/saml-configs/', {});
  const res = unwrap(result) as
    | Paginated<SamlConfigSummary>
    | SamlConfigSummary[];
  return Array.isArray(res) ? res : res.results;
}

export async function getAvailableRoles(): Promise<AvailableRole[]> {
  const result = await client.GET('/api/v2/sso/available-roles/', {});
  return unwrap(result) as unknown as AvailableRole[];
}

export async function getSamlProviders(): Promise<SamlProvider[]> {
  // ADR-136 vendor catalog. Not yet in the generated OpenAPI path types, so
  // call through the any-cast escape hatch this module already uses for
  // schema-looser endpoints (see the file-level eslint-disable).
  const result = await (client.GET as any)('/api/v2/sso/saml-providers/', {});
  return unwrap(result) as unknown as SamlProvider[];
}

export async function getAvailableSchools(): Promise<AvailableSchool[]> {
  const result = await client.GET('/api/v2/sso/available-schools/', {});
  return unwrap(result) as unknown as AvailableSchool[];
}

/**
 * Download Tiaki's SP federation metadata XML for a SCIM configuration's
 * linked SAMLConfig. Phase 1 of the ADR 019 self-service surface — the
 * customer-facing equivalent of GET /saml/metadata/?hnm=<domain>, scoped
 * by the SSO Admin's email domain by IsSSOAdminForDomain.
 *
 * Returns the raw XML as a Blob; callers wrap it in an object URL and
 * trigger a download (see SPDetailsCard).
 *
 * Uses `client.downloadFile()` so the lib's blob/Content-Disposition path
 * applies — same auth-refresh + GPC + vendor-org middleware as JSON calls.
 */
export async function downloadSpMetadataXml(id: number): Promise<Blob> {
  const { blob } = await client.downloadFile(
    `api/v2/sso/scim-configs/${id}/sp-metadata.xml/`,
  );
  return blob;
}

/**
 * Fetch Tiaki's SP federation metadata XML for an arbitrary email domain
 * (`user_hostname_match`), no saved SAMLConfig required. Used by the
 * Add-SAML drawer to surface ACS URL + SP Entity ID + downloadable XML
 * before the IdP-side fields have been filled in.
 *
 * Hits the existing global `/saml/metadata/?hnm=<domain>` endpoint — when
 * no SAMLConfig matches, `SAMLSettingsMixin.get_saml_settings` falls
 * through to the no-config branch and builds settings from
 * `settings.SAML['SP']` + the request domain, so the XML is valid even
 * pre-save.
 */
export async function fetchSpMetadataXml(
  userHostnameMatch: string,
): Promise<string> {
  const { blob } = await client.downloadFile(
    `saml/metadata/?hnm=${encodeURIComponent(userHostnameMatch)}`,
  );
  return blob.text();
}

/**
 * Parse Tiaki's SP federation metadata XML to extract the SP Entity ID
 * and Assertion Consumer Service (ACS) URL. The Entity ID is the
 * `entityID` attribute on the root `EntityDescriptor`; the ACS URL is the
 * `Location` attribute on the first `AssertionConsumerService` binding
 * (Tiaki only emits HTTP-POST, so first == only).
 *
 * Throws `Error` if the XML is malformed or either attribute is missing
 * — keeps the drawer's error path actionable instead of silently showing
 * empty copy rows.
 */
export function parseSpMetadataXml(xml: string): {
  sp_entity_id: string;
  acs_url: string;
} {
  const trimmed = xml.trimStart();
  // Sniff for an HTML response masquerading as 200 — auth redirect, error
  // page, or a misrouted request. DOMParser would still flag it, but with
  // a generic parsererror; surface the actual cause instead.
  if (
    /^<!doctype html/i.test(trimmed) ||
    /^<html[\s>]/i.test(trimmed)
  ) {
    throw new Error(
      `Got an HTML response from /saml/metadata/, not XML. ` +
        `First 120 chars: ${trimmed.slice(0, 120)}`,
    );
  }
  const doc = new DOMParser().parseFromString(xml, 'text/xml');
  const parseError = doc.querySelector('parsererror');
  if (parseError) {
    const detail = (parseError.textContent ?? '').trim();
    const preview = trimmed.slice(0, 200);
    throw new Error(
      `SP metadata XML is not well-formed${detail ? `: ${detail}` : ''}. ` +
        `Response started with: ${preview}`,
    );
  }
  const entityDescriptor =
    doc.getElementsByTagNameNS('*', 'EntityDescriptor')[0] ??
    doc.documentElement;
  const spEntityId = entityDescriptor?.getAttribute('entityID') ?? '';
  const acsNode =
    doc.getElementsByTagNameNS('*', 'AssertionConsumerService')[0];
  const acsUrl = acsNode?.getAttribute('Location') ?? '';
  if (!spEntityId || !acsUrl) {
    throw new Error(
      'SP metadata XML is missing entityID or AssertionConsumerService.',
    );
  }
  return { sp_entity_id: spEntityId, acs_url: acsUrl };
}

/**
 * Extract `<AttributeValue>` text content for a named SAML attribute
 * (e.g. "groups") from raw IdP federation metadata XML. Used by the
 * Add-SAML drawer to prepopulate Site / Role mapping tables with the
 * group names the IdP advertises for the configured claim.
 *
 * Returns an empty array when the XML doesn't parse, the claim name is
 * empty, or no matching `<Attribute Name="…">` element carries any
 * non-empty `<AttributeValue>` children. The caller decides whether an
 * empty result should clear the table or be ignored.
 *
 * Namespace-agnostic — IdPs vary on whether `Attribute` is in the SAML
 * assertion namespace (`urn:oasis:names:tc:SAML:2.0:assertion`), the
 * metadata namespace, or unprefixed.
 */
export function extractClaimValuesFromMetadata(
  xml: string,
  claimName: string,
): string[] {
  if (!xml || !claimName) return [];
  const doc = new DOMParser().parseFromString(xml, 'text/xml');
  if (doc.querySelector('parsererror')) return [];
  const attributes = Array.from(
    doc.getElementsByTagNameNS('*', 'Attribute'),
  );
  const values = new Set<string>();
  for (const attr of attributes) {
    if (attr.getAttribute('Name') !== claimName) continue;
    const valueNodes = attr.getElementsByTagNameNS('*', 'AttributeValue');
    for (const v of Array.from(valueNodes)) {
      const text = (v.textContent ?? '').trim();
      if (text) values.add(text);
    }
  }
  return Array.from(values);
}

// ── SAML self-service wizard ─────────────────────────────────────────

/**
 * IdP vendor catalog entry (ADR-136). Returned by
 * `GET /api/v2/sso/saml-providers/`; populates the Sign-in drawer's
 * provider selector and routes the per-vendor help link.
 */
export interface SamlProvider {
  key: string;
  name: string;
  guide_slug: string;
  sort_order: number;
}

/**
 * Full SAMLConfig detail returned by the customer-facing wizard endpoint.
 * Matches `SAMLConfigDetailSerializer` in
 * server/apps/scim/api/saml_serializers.py.
 */
export interface SamlConfigDetail {
  id: number;
  name: string;
  user_hostname_match: string;
  type: string | null;
  app_name: string | null;
  idp_metadata_uri: string | null;
  sign_on_server_uri: string | null;
  idp_x509_cert_contents: string | null;
  /** @deprecated Kept for backward compat; the UI does not surface it. */
  campus_admin_group_name: string;
  /** @deprecated Kept for backward compat; the UI does not surface it. */
  district_admin_group_name: string;
  /** @deprecated Kept for backward compat; the UI does not surface it. */
  responder_read_only_group_name: string;
  /** @deprecated Kept for backward compat; the UI does not surface it. */
  responder_group_name: string;
  /** @deprecated Kept for backward compat; the UI does not surface it. */
  read_only_user_group_name: string;
  site_assignment_name: string;
  role_assignment_name: string;
  /** @deprecated Kept for backward compat; the UI does not surface it. */
  no_site_means_all: boolean;
  add_hostname_in_entity_id: boolean;
  /** ADR-136: IdP vendor key (e.g. 'adfs', 'entra'); null when unset. */
  provider: string | null;
  /** ADR-136: nested vendor metadata (read-only). */
  provider_detail: SamlProvider | null;
  rotation_certs: { id: number; cert_excerpt: string }[];
  /** SAML-native group → role mappings. Replace-style on PATCH. */
  role_mappings: RoleMapping[];
  /** SAML-native group → site mappings. Replace-style on PATCH. */
  school_mappings: SchoolMapping[];
}

export interface SamlConfigWrite {
  name?: string;
  user_hostname_match?: string;
  app_name?: string | null;
  type?: string | null;
  /** ADR-136: IdP vendor key; validated against the active catalog. */
  provider?: string | null;
  idp_metadata_uri?: string | null;
  sign_on_server_uri?: string | null;
  idp_x509_cert_contents?: string | null;
  site_assignment_name?: string;
  role_assignment_name?: string;
  add_hostname_in_entity_id?: boolean;
  /** SAML-native group → role mappings. Replace-style on PATCH. */
  role_mappings?: RoleMapping[];
  /** SAML-native group → site mappings. Replace-style on PATCH. */
  school_mappings?: SchoolMapping[];
  /**
   * Paste/upload the IdP federation metadata XML; the backend extracts
   * Entity ID, Sign-On URL, and the signing certificate. Explicit
   * `idp_metadata_uri` / `sign_on_server_uri` / `idp_x509_cert_contents`
   * fields override the parsed values when both are provided.
   */
  raw_metadata_xml?: string;
}

export interface ParsedSamlMetadata {
  entity_id: string;
  sso_url: string;
  slo_url: string | null;
  /** Primary cert is index 0; rotation candidates (if any) follow. */
  certs: string[];
}

export interface TestSsoResponse {
  success: boolean;
  message: string;
  redirect_url?: string;
}

export async function getSamlConfig(id: number): Promise<SamlConfigDetail> {
  const result = await client.GET('/api/v2/sso/saml-configs/{id}/', {
    params: { path: { id } as never },
  });
  return unwrap(result) as unknown as SamlConfigDetail;
}

export async function createSamlConfig(
  payload: SamlConfigWrite,
): Promise<SamlConfigDetail> {
  const result = await client.POST('/api/v2/sso/saml-configs/', {
    body: payload as never,
  });
  return unwrap(result) as unknown as SamlConfigDetail;
}

export async function updateSamlConfig(
  id: number,
  payload: SamlConfigWrite,
): Promise<SamlConfigDetail> {
  const result = await client.PATCH('/api/v2/sso/saml-configs/{id}/', {
    params: { path: { id } as never },
    body: payload as never,
  });
  return unwrap(result) as unknown as SamlConfigDetail;
}

/**
 * Permanently delete a SAMLConfig. Drops the IdP metadata, all SAML
 * group→role / group→site mappings, and any rotation certs associated
 * with this config (cascade on the backend FK). Cannot be undone — the
 * caller is responsible for a confirmation prompt.
 */
export async function deleteSamlConfig(id: number): Promise<void> {
  const result = await client.DELETE('/api/v2/sso/saml-configs/{id}/', {
    params: { path: { id } as never },
  });
  const __resp = result as { error?: unknown };
  if (__resp.error !== undefined) throw __resp.error;
}

/**
 * Preview the parsed values from a SAML 2.0 federation metadata XML
 * before saving. The backend never persists from this endpoint; the
 * wizard renders the result inline so the admin can verify before commit.
 */
export async function parseSamlMetadataXml(
  metadataXml: string,
): Promise<ParsedSamlMetadata> {
  const result = await client.POST('/api/v2/sso/saml-configs/parse-metadata/', {
    body: { metadata_xml: metadataXml } as never,
  });
  return unwrap(result) as unknown as ParsedSamlMetadata;
}

// Schema-divergence helper: a few sso endpoints have body required by the
// schema but were originally written body-less; the path-shape `{id: string}`
// also doesn't match our numeric IDs without coercion. Route through the
// permissive surface so the URL still gates type-wise but the params/body
// shape isn't fought.
const ssoPost = client.POST as unknown as (
  path: string,
  init: unknown,
) => ReturnType<typeof client.POST>;

/**
 * Initiate a real SAML AuthnRequest round-trip to verify the saved
 * config works against the IdP. The backend builds the AuthnRequest URL;
 * the wizard opens it in a new tab and lets the existing
 * `CompleteAuthenticationView` callback finish the flow in the same
 * browser session. On any setup error the response carries
 * `success=false` with a human-readable message rather than an HTTP
 * error so the wizard can render the error inline.
 */
export async function testSamlSso(id: number): Promise<TestSsoResponse> {
  const result = await ssoPost('/api/v2/sso/saml-configs/{id}/test-sso/', {
    params: { path: { id: String(id) } },
    body: {},
  });
  return unwrap(result) as unknown as TestSsoResponse;
}

// ── SCIM self-service wizard ─────────────────────────────────────────

export interface RotateBearerResponse {
  generated_bearer_token: string;
  message: string;
}

export interface UploadGoogleCredsResponse {
  detail: string;
  client_id: string;
  client_email: string;
  admin_email: string;
}

/**
 * Rotate the SCIM bearer token for the configuration. The backend
 * generates a fresh URL-safe 48-byte token, persists only its SHA-256
 * hash, and returns the plaintext **once**. The caller is expected to
 * surface the value in a one-time reveal modal so the admin can copy
 * it into their IdP — Tiaki will not display it again on subsequent
 * reads.
 */
export async function rotateScimBearerToken(
  id: number,
): Promise<RotateBearerResponse> {
  const result = await ssoPost(
    '/api/v2/sso/scim-configs/{id}/rotate-bearer/',
    { params: { path: { id: String(id) } }, body: {} },
  );
  return unwrap(result) as unknown as RotateBearerResponse;
}

/**
 * Upload a Google service-account JSON file plus the super-admin
 * `admin_email` to delegate as. The backend validates the JSON shape
 * (must be `type=service_account` with `client_email` and
 * `private_key`) and saves into `SCIMConfiguration.idp_credentials`.
 *
 * The returned `client_id` is the Google "Unique ID" the admin needs
 * to paste into Google Admin's *Domain-wide delegation* screen.
 *
 * FormData uploads stay on `client.ky` — `openapi-fetch` accepts a
 * FormData body via its body field, but ky's `FormData` handling is
 * already what the auth-refresh + GPC + vendor-org middleware was
 * tested against.
 */
export async function uploadGoogleScimCreds(
  id: number,
  file: File,
  adminEmail: string,
): Promise<UploadGoogleCredsResponse> {
  const form = new FormData();
  form.append('credentials_json', file);
  form.append('admin_email', adminEmail);
  const result = await client.POST(
    '/api/v2/sso/scim-configs/{id}/upload-google-creds/',
    {
      params: { path: { id } as never },
      body: form as never,
    },
  );
  return unwrap(result) as unknown as UploadGoogleCredsResponse;
}

// ── Phase 4: rotation certs + CSV mapping import ────────────

export interface RotationCertSummary {
  id: number;
  cert_excerpt: string;
}

export interface ImportMappingsPreview {
  role_mappings: RoleMapping[];
  school_mappings: SchoolMapping[];
  role_count: number;
  school_count: number;
  dry_run: boolean;
  /** Present only on the actual replace (non-dry-run) response. */
  detail?: string;
}

export interface ImportMappingsError {
  detail: string;
  errors?: { line: number; error: string }[];
}

/** Add a rotation cert to a SAMLConfig (primary + one only). */
export async function addSamlRotationCert(
  samlConfigId: number,
  certBase64: string,
): Promise<RotationCertSummary> {
  const result = await client.POST(
    '/api/v2/sso/saml-configs/{id}/rotation-certs/',
    {
      params: { path: { id: samlConfigId } as never },
      body: { cert: certBase64 } as never,
    },
  );
  return unwrap(result) as unknown as RotationCertSummary;
}

/** Remove a rotation cert by its row id. */
export async function deleteSamlRotationCert(
  samlConfigId: number,
  certId: number,
): Promise<void> {
  const result = await client.DELETE(
    '/api/v2/sso/saml-configs/{id}/rotation-certs/{cert_id}/',
    {
      params: {
        path: { id: samlConfigId, cert_id: certId } as never,
      },
    },
  );
  const __resp = result as { error?: unknown }; if (__resp.error !== undefined) throw __resp.error;
}

/**
 * Bulk-import role/site mappings from a CSV. The CSV must have a
 * header row with columns from {idp_group, tiaki_role, tiaki_school}
 * (idp_group is required). Pass ``dryRun=true`` to preview without
 * persisting.
 *
 * Always uploads as multipart/form-data with the file under the
 * ``mappings_csv`` field so we don't have to fight the apiClient over
 * Content-Type. String inputs are wrapped in a synthetic File.
 *
 * Throws on a 400 with the parsed error envelope so the UI can render
 * per-row errors inline.
 */
export async function importScimMappings(
  scimConfigId: number,
  csv: string | File,
  dryRun = false,
): Promise<ImportMappingsPreview> {
  const file =
    csv instanceof File
      ? csv
      : new File([csv], 'mappings.csv', { type: 'text/csv' });
  const form = new FormData();
  form.append('mappings_csv', file);
  const result = await client.POST(
    '/api/v2/sso/scim-configs/{id}/import-mappings/',
    {
      params: {
        path: { id: scimConfigId } as never,
        query: (dryRun ? { dry_run: 'true' } : {}) as never,
      },
      body: form as never,
    },
  );
  return unwrap(result) as unknown as ImportMappingsPreview;
}

/**
 * Bulk-import SAML role/site mappings from CSV. Same contract
 * as `importScimMappings` but writes to SAMLRoleConfig/SAMLSchoolConfig
 * instead of SCIMConfiguration JSON.
 */
export async function importSamlMappings(
  samlConfigId: number,
  csv: string | File,
  dryRun = false,
): Promise<ImportMappingsPreview> {
  const file =
    csv instanceof File
      ? csv
      : new File([csv], 'mappings.csv', { type: 'text/csv' });
  const form = new FormData();
  form.append('mappings_csv', file);
  const result = await client.POST(
    '/api/v2/sso/saml-configs/{id}/import-mappings/',
    {
      params: {
        path: { id: samlConfigId } as never,
        query: (dryRun ? { dry_run: 'true' } : {}) as never,
      },
      body: form as never,
    },
  );
  return unwrap(result) as unknown as ImportMappingsPreview;
}
